import { Injectable } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import Brand, { BrandingCompany } from 'mbs-ui-shared@models/Brand';
import BrandImage from 'mbs-ui-shared@models/BrandImage';
import BrandLicense from 'mbs-ui-shared@models/BrandLicense';
import BrandVersion from 'mbs-ui-shared@models/BrandVersion';
import { BrandsService } from 'mbs-ui-shared@services/brands.service';
import { BrandsImagesService } from 'mbs-ui-shared@services/brandsImages.service';
import { BehaviorSubject, Observable, finalize, map, startWith, switchMap, tap } from 'rxjs';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class BrandsFacade {
  private myLicenses$: BehaviorSubject<BrandLicense[]> = new BehaviorSubject<BrandLicense[]>(null);
  public licenses$ = this.myLicenses$.asObservable();

  public loading$ = this.service.loading$;
  public entities$ = this.service.entities$;

  public imagesRequestedId = '';
  public imagesIsLoading = false;
  public imagesData$: BehaviorSubject<{ images: BrandImage[]; brandId: string }> = new BehaviorSubject<{
    images: BrandImage[];
    brandId: string;
  }>({
    images: [],
    brandId: ''
  });

  constructor(private service: BrandsService, private auth: AuthFacade, private imagesService: BrandsImagesService) {}

  public getAll$(): Observable<Brand[]> {
    return this.service.getAll();
  }

  public getById$(id: string): Observable<Brand> {
    return this.service.getByKey(id).pipe(startWith({ id } as Brand));
  }

  public add$(brand: Brand): Observable<Brand> {
    return this.service.add(brand).pipe(switchMap((brand) => this.service.getAll().pipe(map(() => brand))));
  }

  public update$(brand: Brand): Observable<Brand> {
    return this.service.update(brand).pipe(switchMap((brand) => this.service.getAll().pipe(map(() => brand))));
  }

  public delete$(id: string): Observable<boolean> {
    return this.service.delete(id).pipe(
      switchMap(() => this.service.getAll()),
      map(() => true)
    );
  }

  public getVersions$(brandId: string): Observable<BrandVersion[]> {
    return this.service.getVersions$(brandId);
  }

  public getDownloadLink$(brandId: string, code: string): Observable<string> {
    return this.service.getDownloadLink$(brandId, code);
  }

  public getDefault$(): Observable<Brand> {
    return this.service.getDefault$();
  }

  public getActive$(): Observable<Brand[]> {
    return this.service.getActive$();
  }

  public getAllBrandingCompanies$(): Observable<BrandingCompany[]> {
    return this.service.getAllBrandingCompanies$();
  }

  public checkSymbols$(value): Observable<any> {
    return this.service.checkSymbols$(value);
  }

  // licenses

  public getLicenses(): void {
    this.service.getLicenses().subscribe((licenses: BrandLicense[]) => {
      this.myLicenses$.next(licenses);
    });
  }

  public assignLicenseToBrand$(licenseId: string, brandId: string): Observable<void> {
    return this.service
      .assignLicenseToBrand$(licenseId, brandId)
      .pipe(switchMap((res) => this.auth.fetchCurrentUser$.pipe(map(() => res))));
  }

  public deassignLicenseFromBrand$(licenseId: string): Observable<void> {
    return this.service.deassignLicenseFromBrand$(licenseId);
  }

  // images

  imagesGet$(brandId: string): Observable<BrandImage[]> {
    this.imagesRequestedId = brandId;
    this.imagesIsLoading = true;
    return this.imagesService.get(brandId).pipe(
      tap((data) => this.imagesData$.next({ images: data, brandId })),
      finalize((): void => {
        this.imagesIsLoading = false;
      })
    );
  }

  imagesUpdate$(brandId: string, data: BrandImage[]): Observable<any> {
    this.imagesRequestedId = brandId;
    return this.imagesService.update(brandId, data);
  }

  imagesAdd$(brandId: string, formData: FormData): Observable<any> {
    return this.imagesService.add(brandId, formData);
  }

  imagesClear() {
    this.imagesData$.next({ brandId: '', images: [] });
  }
}
